import * as React from 'react';
import { makeStyles, shorthands, Spinner, Title1, Toaster, tokens } from '@fluentui/react-components';
import {
  Home20Filled,
  Home20Regular,
  Library20Filled,
  Library20Regular,
  QuestionRegular,
  Ribbon20Filled,
  Ribbon20Regular,
  Wand20Filled,
  Wand20Regular,
} from '@fluentui/react-icons';
import loadable from '@loadable/component';
import { defineMessage, useIntl } from 'react-intl';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { type User, useConfigQuery, useUserQuery } from '@foundershub/api-client';
import { AssumeUserBanner, DemoUserBanner, Header, NavOptions, SkipToMain } from '../components';
import { CenteredLoadingSpinner } from '../components/CenteredLoadingSpinner';
import { DrawerContext } from '../components/FoundersHubUserOverlayDrawer';
import { TabNavigation } from '../components/TabNavigation';
import type { NavigationTab } from '../components/TabNavigationTypes';
import { breakpoints, screenSizes } from '../modules/breakpoints';
import usePathnameRedirect from '../modules/usePathnameRedirect';
import { useSessionStorage } from '../modules/useSessionStorage';
import { useAccountRequired, useFeatureFlagEnabled, useUserRequired } from '../store';

const FirstTimeModal = loadable(() => import(/* webpackChunkName: "first-time" */ '../pages/FirstTimeModal'));
const SupportDrawer = loadable(
  () => import(/* webpackChunkName: "support-drawer" */ '../pages/SupportForm/SupportDrawer'),
  {
    fallback: <CenteredLoadingSpinner />,
  }
);
const LevelUpCelebrationModal = loadable(
  () => import(/* webpackChunkName: "level-up-celebration" */ '../pages/LevelUpModal/LevelUpCelebrationModal')
);
const AzureCreditsExpireSoonModal = loadable(
  () => import(/* webpackChunkName: "azure-credits-expire-soon" */ '../pages/LevelUpModal/AzureCreditsExpireSoonModal')
);

const ActivateAzureSoonModal = loadable(
  () => import(/* webpackChunkName: "activate-azure-soon" */ '../pages/LevelUpModal/ActivateAzureSoonModal')
);

interface Props {
  children: React.ReactNode;
}

export default function AccountRequiredRedirectLayout(props: Props) {
  useUserRequired();
  const { data: user, isLoading, isFetching } = useUserQuery();

  if ((isLoading || isFetching) && user?.accounts.length === 0) {
    return <Spinner />;
  }

  if (user?.accounts.length === 0) {
    return <Redirect to="/apply" />;
  }

  return <Layout {...props} />;
}

function Layout({ children }: Props) {
  const location = useLocation();
  const classes = useStyles();
  const intl = useIntl();
  const account = useAccountRequired();
  const user = useUserRequired();
  const history = useHistory();
  const isSettingsRoute = history.location.pathname.split('/')?.[1] === 'settings';
  const [toasterHorizontalOffset] = React.useState<number>(10);
  const [toasterVerticalOffset] = React.useState<number>(39);

  const buildWithAiAndDiscover = useFeatureFlagEnabled('build-with-ai-and-discover');
  const phase2Enabled = useFeatureFlagEnabled('phase2_enabled');
  const { data: featureFlagData } = useConfigQuery();

  const [showFirstTimeModal, setShowFirstTimeModal] = React.useState<boolean>(false);
  const { setShowDrawer, setChildren } = React.useContext(DrawerContext);
  const [showAzureCreditsExpireSoonModal, setShowAzureCreditsExpireSoonModal] = useSessionStorage(
    'show-azure-credits-expire-soon-modal',
    true
  );
  const [showActivateAzureSoonModal, setShowActivateAzureSoonModal] = useSessionStorage(
    'show-activate-azure-soon-modal',
    true
  );
  const redirectBasedOnPathname = usePathnameRedirect();

  function setShowSupportDrawer() {
    setShowDrawer(true);
    setChildren(<SupportDrawer />);
    redirectBasedOnPathname();
  }

  React.useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.has('firsttime')) {
      setShowFirstTimeModal(true);
    } else if ((user?.accounts.length || 0) > 0) {
      setShowFirstTimeModal(shouldShowFirsttimeModal(user));
    }
  }, []);

  const headerOptions = [
    ...NavOptions,
    {
      title: defineMessage({ defaultMessage: 'Help', description: 'help' }),
      icon: <QuestionRegular />,
      onClick: setShowSupportDrawer,
    },
  ];

  const tabs: Array<NavigationTab> = [
    {
      id: '/',
      name: intl.formatMessage(companyProfileNavLabel),
      iconRegular: <Home20Regular />,
      iconFilled: <Home20Filled />,
    },
    {
      id: '/benefits',
      name: intl.formatMessage(benefitsNavLabel),
      iconRegular: <Ribbon20Regular />,
      iconFilled: <Ribbon20Filled />,
    },
  ];

  const tabsWithAiAndDiscover: Array<NavigationTab> = [
    ...tabs,
    {
      id: '/ai',
      name: intl.formatMessage(buildWithAINavLabel),
      iconRegular: <Wand20Regular />,
      iconFilled: <Wand20Filled />,
    },
    {
      id: '/discover',
      name: intl.formatMessage(discoverNavLabel),
      iconRegular: <Library20Regular />,
      iconFilled: <Library20Filled />,
    },
  ];

  if (featureFlagData === undefined) {
    return <CenteredLoadingSpinner />;
  }

  return (
    <div className={classes.root}>
      <SkipToMain id="main" />
      {showFirstTimeModal && <FirstTimeModal closeModal={() => setShowFirstTimeModal(false)} />}
      {user.perms.includes('is_assumed_user') ? <AssumeUserBanner /> : null}
      {user.perms.includes('is_demo_user') ? <DemoUserBanner /> : null}
      <Header options={headerOptions} />
      <div className={classes.main}>
        <main id="main" className={isSettingsRoute ? classes.settingsMainContent : classes.mainContent}>
          <Toaster
            offset={{ horizontal: toasterHorizontalOffset, vertical: toasterVerticalOffset }}
            toasterId={account.id}
          />
          {!isSettingsRoute && (
            <div className={classes.navHeader}>
              <Title1 as="h1">{account.name}</Title1>
              {!(phase2Enabled && account.has_graduated) && (
                <div className={classes.tabs}>
                  <TabNavigation tabs={buildWithAiAndDiscover ? tabsWithAiAndDiscover : tabs} />
                </div>
              )}
            </div>
          )}
          {children}
        </main>
        {!showFirstTimeModal &&
        phase2Enabled &&
        user.levelup_requests_to_ack?.some((request) => request.account_id === account.id) ? (
          <LevelUpCelebrationModal />
        ) : null}
        {!showFirstTimeModal && phase2Enabled && account.show_activate_az_modal && showActivateAzureSoonModal ? (
          <ActivateAzureSoonModal closeModal={() => setShowActivateAzureSoonModal(false)} />
        ) : null}
        {!showFirstTimeModal &&
        phase2Enabled &&
        !account.show_activate_az_modal &&
        account.has_upcoming_graduation &&
        showAzureCreditsExpireSoonModal ? (
          <AzureCreditsExpireSoonModal closeModal={() => setShowAzureCreditsExpireSoonModal(false)} />
        ) : null}
      </div>
    </div>
  );
}

function shouldShowFirsttimeModal(user: User): boolean {
  return !user.has_signed_agreement || user.accounts.some((account) => !account.signed_agreement_on);
}

const useStyles = makeStyles({
  root: {
    backgroundColor: tokens.colorNeutralBackground2,
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    maxWidth: '100vw',
    alignItems: 'stretch',
  },
  main: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    flexBasis: 0,
    flexGrow: 1,
  },
  mainContent: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: 0,
    flexGrow: 2,
    height: '100%',
    ...shorthands.margin(0, 0, '5%', 0),
    overflowX: 'hidden', // needed for when the vertical scrollbar is present on benefits page
    maxWidth: 'calc(100vw - 10%)',
    ...shorthands.padding(0, '5%'),
    ...breakpoints.xxl({ maxWidth: `calc(${screenSizes.xxl} - 10%)`, ...shorthands.margin(0, 'auto', '5%', 'auto') }),
  },
  settingsMainContent: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: 0,
    flexGrow: 2,
  },
  navHeader: {
    ...shorthands.padding(tokens.spacingVerticalXL, 0),
    maxWidth: '100vw',
  },
  tabs: {
    paddingTop: tokens.spacingVerticalXL,
  },
});

const benefitsNavLabel = defineMessage({
  defaultMessage: 'Benefits',
  description: 'Benefits navigation label in main nav drawer',
});

const buildWithAINavLabel = defineMessage({
  defaultMessage: 'Build with AI',
  description: 'Build with AI page navigation label in main nav drawer',
});

const companyProfileNavLabel = defineMessage({
  defaultMessage: 'Home',
  description: 'Company Profile navigation label in main Nav drawer',
});

const discoverNavLabel = defineMessage({
  defaultMessage: 'Discover',
  description: 'Discover page navigation label in main nav drawer',
});
